/**
 * Der Monatsbericht als Ansicht, wie ihn GET /api/dokumente/:id/bwa/bericht
 * liefert. Spiegel von server/src/bwa/auswertung/berichtansicht.ts und
 * berichtansicht-laden.ts; Betraege in Cent, Quoten in Prozent.
 */

export type Zeitraum = 'periode' | 'lfd_jahr' | 'zwoelf_monate'

export interface Wertpaar {
  jetzt: number | null
  vorjahr: number | null
}

export interface Kennzahlen {
  jetzt: string
  vorjahr: string
  umsatz: Wertpaar
  rohertrag: Wertpaar
  rohertragsquote: Wertpaar
  ergebnis: Wertpaar
}

export interface Saeule {
  periode: string
  name: string
  jahr: number
  wert: number | null
  vorjahr: number | null
  /** Nur bei aufgelaufenen Werten: Stand bis zur Periode davor, hell gezeichnet. */
  basis?: number | null
  basisVorjahr?: number | null
}

export interface Saeulen {
  einheit: 'cent' | 'prozent'
  saeulen: Saeule[]
}

export interface Verlauf extends Saeulen {
  art: 'bestand' | 'zeitraum' | 'quote'
  messung: 'saldo' | 'periode' | 'lfd_jahr' | 'zwoelf_monate' | 'quote_lfd_jahr'
  /** Bei Betraegen eines Kontos oder einer Position beide Sichten, sonst null. */
  sichten: { jePeriode: Saeule[]; aufgelaufen: Saeule[] } | null
}

export interface Kostenzeile {
  schluessel: string
  name: string
  quote: number
  quoteVorjahr: number | null
  betrag: number
  betragVorjahr: number | null
  /** Quote deutlich neben dem Ueblichen (Median der Perioden davor); nur im Zeitraum Periode. */
  auffaellig: { ueblich: number; perioden: number } | null
}

export interface Kosten {
  zeilen: Kostenzeile[]
  hinweis: string | null
}

export interface Diagramm {
  schluessel: string
  name: string
  gruppe: 'ergebnis' | 'kosten' | 'liquiditaet'
  art: 'zeitraum' | 'bestand'
  reihen: Record<Zeitraum, Saeulen>
}

export type Gruppe = 'beratung' | 'buchhaltung' | 'unveraendert' | 'erledigt'

export interface Punkt {
  id: string
  gruppe: Gruppe
  prioritaet: 'hoch' | 'mittel' | 'niedrig'
  vorlaeufig: boolean
  titel: string
  text: string
  neu: boolean
  seit: string | null
  messpunkt: { name: string; jetzt: string | null; beurteilt: { am: string; wert: string } | null } | null
  /** Bei unveraenderten: heutiger Stand des Messpunkts; der Text ist aus der Beurteilung von damals. */
  /** lage: Satz der KI, wie es heute steht (aeltere Berichte: null); zeile: die Zahlen dazu, vom Code. */
  stand: { lage: string | null; zeile: string | null; beurteilt: string } | null
  verlauf: Verlauf | null
  ohneVerlauf: string | null
  belege: { was: string; wert: string; bezug: string }[]
}

export interface Monatsbericht {
  kiLaufId: string
  periode: string
  art: { bericht: string; vorperiode: string }
  stichtag: string
  zeitraeume: { zeitraum: Zeitraum; name: string }[]
  kennzahlen: Record<Zeitraum, Kennzahlen>
  diagramme: Diagramm[]
  kosten: Record<Zeitraum, Kosten>
  vorjahrHinweis: string | null
  kurzfazit: string | null
  hinweis: string | null
  punkte: Punkt[]
}

/** Name einer Gruppe; "Erledigt seit dem Vorquartal" bei Quartals BWAs. */
export function gruppeName(g: Gruppe, vorperiode: string): string {
  return {
    beratung: 'Beratungsanlässe',
    buchhaltung: 'Für die Buchhaltung',
    unveraendert: 'Bekannt und unverändert',
    erledigt: `Erledigt seit dem ${vorperiode}`,
  }[g]
}
